import React, { useState, useEffect, useCallback } from 'react';
import { message, Spin, Button, Row, Col, Descriptions, Radio, Rate, Input, Select, Popconfirm, Checkbox } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const sportsPerformanceConfig = [
  { name: '跳绳', code: 'ropeSkipping', unit: '次/分钟' },
  { name: '50米', code: 'fiftyMeters', unit: '秒' },
  { name: '仰卧起坐', code: 'sitUps', unit: '次/分钟' },
  { name: '坐位体前屈', code: 'sitAndReach', unit: '厘米' },
  { name: '立定跳远', code: 'standingLongJump', unit: '厘米' },
];

const motorSkillsConfig = [
  { name: '心肺耐力', code: 'cardioEndurance' },
  { name: '肌肉力量', code: 'muscleStrength' },
  { name: '平衡能力', code: 'balance' },
  { name: '柔韧性', code: 'flexibility' },
  { name: '协调性', code: 'coordination' },
  { name: '专注力', code: 'concentration' },
  { name: '爆发力', code: 'power' },
];

// 原来的硬编码配置已移除，现在使用从接口获取的数据

const StudentArchive = ({ memberId, memberInfo, onSaveSuccess, readonly = false }) => {
  const [archiveData, setArchiveData] = useState({});
  const [expandData, setExpandData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showHeightDetails, setShowHeightDetails] = useState(false);
  const [expertAdviceViewMode, setExpertAdviceViewMode] = useState(true);
  const [postureAssessmentData, setPostureAssessmentData] = useState({});
  const { apiClient } = useAuth();

  // 富文本编辑器配置
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'blockquote', 'code-block',
    'list', 'bullet', 'ordered',
    'indent',
    'link', 'image', 'video'
  ];

  // HTML解码函数
  const decodeHtml = (html) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  // 将纯文本或旧格式统一转成安全的 HTML，用于展示模式
  const normalizeRichText = (raw) => {
    if (!raw) return '';
    let content = String(raw);
    // 先进行 HTML 解码，兼容后端存的转义内容
    try {
      content = decodeHtml(content);
    } catch (e) {
      // ignore
    }
    const trimmed = content.trim();
    if (!trimmed) return '';

    // 简单判断是否已经是 HTML 结构（包括 Quill 生成的内容）
    const htmlLikePattern = /<([a-zA-Z][^>]*)>/;
    if (
      htmlLikePattern.test(trimmed) ||
      /&nbsp;/.test(trimmed) ||
      trimmed.startsWith('<p') ||
      trimmed.startsWith('<div')
    ) {
      return trimmed;
    }

    // 纯文本：需要做转义并按换行转成 <p>/<br>
    const escaped = trimmed
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const lines = escaped.split(/\r?\n/);
    if (lines.length === 1) {
      return `<p>${lines[0] || ''}</p>`;
    }
    return lines
      .map((line) => `<p>${line || ''}</p>`)
      .join('');
  };

  // 获取姿态评估配置数据
  const fetchPostureAssessmentData = useCallback(async () => {
    if (!apiClient) return;
    
    try {
      const response = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'POSTURE_ASSESSMENT',
        colName: 'POSTURE_ASSESSMENT'
      });
      
      if (response.data.code === 200 && response.data.data) {
        // 解析JSON字符串数据
        const rawData = typeof response.data.data === 'string' 
          ? JSON.parse(response.data.data) 
          : response.data.data;
        
        // 按parentEnumCode分组
        const groupedData = rawData.reduce((acc, item) => {
          const parentCode = item.parentEnumCode || '其他';
          if (!acc[parentCode]) {
            acc[parentCode] = [];
          }
          acc[parentCode].push({
            name: item.enumName,
            code: item.enumCode
          });
          return acc;
        }, {});
        
        setPostureAssessmentData(groupedData);
      }
    } catch (error) {
      console.error('获取姿态评估配置失败:', error);
      message.error('获取姿态评估配置失败');
    }
  }, [apiClient]);

  const fetchExpandData = useCallback(async (id) => {
    if (!id || !apiClient) return;
    try {
      setLoading(true);
      const response = await apiClient.get(`/sports-server-prod/taller.api.v1/growth-expand/queryByBabyIdGrouped?babyId=${id}`);
      if (response.data.code === 200 && response.data.data) {
        const groupedData = response.data.data.reduce((acc, group) => {
          acc[group.expandGroup] = group.expandList.reduce((groupAcc, item) => {
            groupAcc[item.expandCode] = item.expandValue;
            return groupAcc;
          }, {});
          return acc;
        }, {});
        setExpandData(groupedData);
      } else {
        setExpandData({});
      }
    } catch (error) {
      console.error('获取扩展档案失败:', error);
      message.error('获取扩展档案失败');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  // 获取学员档案信息
  const fetchArchiveData = useCallback(async (id) => {
    if (!id || !apiClient) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/sports-server-prod/taller.api.v1/growth/queryById?babyId=${id}&searchType=first`);
      
      if (response.data.code === 200 && response.data.data && response.data.data.length > 0) {
        const data = response.data.data[0];
        // 将逗号分割的字符串转为数组用于多选展示
        const expectedGoalArray = data.expectedGoal 
          ? data.expectedGoal.split(',').filter(item => item.trim() !== '') 
          : [];
        setArchiveData({
          ...data,
          desiredHeight: data.desiredHeight || '',
          expectedGoal: expectedGoalArray
        });
      } else {
        // 如果没有找到档案数据，初始化为空对象
        setArchiveData({
          height: '',
          weight: '',
          bmi: '',
          heightTh: '',
          weightTh: '',
          heightF: '',
          heightM: '',
          geneticHeight: '',
          boneAge: '',
          desiredHeight: '',
          futureHeight: '',
          expectedGoal: [],
          insResultBoneAge: '',
          insResultPosture: '',
          insResultLocomotion: '',
          insResultOther: '',
          personality: '',
          hobby: '',
          locomotionLevel: '',
          focusPowerLevel: '',
          futureHeightTh1: '',
          futureHeightTh2: '',
          futureHeightTh3: '',
          futureHeightTh4: '',
        });
        if (!readonly) {
          message.info('该学员暂无档案数据');
        }
      }
    } catch (error) {
      console.error('获取档案失败:', error);
      if (!readonly) {
        message.error('获取档案失败');
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient, readonly]);

  const saveExpandData = async (id) => {
    if (!id) return;

    try {
      const payload = Object.keys(expandData).map(groupName => ({
        babyId: id,
        expandGroup: groupName,
        expandList: Object.keys(expandData[groupName]).map(itemCode => {
          // 根据groupName和itemCode查找对应的名称
          let expandName = '';
          
          // 查找不同配置组中的对应名称
          switch (groupName) {
            case '体育成绩':
              const sportsItem = sportsPerformanceConfig.find(item => item.code === itemCode);
              if (sportsItem) expandName = sportsItem.name;
              break;
            case '运动能力评估':
              const motorItem = motorSkillsConfig.find(item => item.code === itemCode);
              if (motorItem) expandName = motorItem.name;
              break;
            case '体态评估':
              // 使用动态获取的姿态评估数据查找名称
              Object.values(postureAssessmentData).forEach(categoryItems => {
                const foundItem = categoryItems.find(item => item.code === itemCode);
                if (foundItem) {
                  expandName = foundItem.name;
                }
              });
              break;
            case '运动情况':
              if (itemCode === 'exerciseCycle') expandName = '运动周期';
              if (itemCode === 'exerciseIntensity') expandName = '运动强度';
              break;
          }
          
          return {
            babyId: id,
            expandCode: itemCode,
            expandName: expandName,
            expandValue: expandData[groupName][itemCode],
            expandGroup: groupName,
          };
        })
      }));

      const response = await apiClient.post('/sports-server-prod/taller.api.v1/growth-expand/batchSaveOrUpdate', payload);

      if (response.data.code === 200) {
        await fetchExpandData(id);
        return true;
      } else {
        message.error(response.data.msg || '保存失败');
        return false;
      }
    } catch (error) {
      console.error('保存扩展信息错误:', error);
      message.error('保存扩展信息失败');
      return false;
    }
  };

  // 保存学员档案
  const saveArchiveData = async () => {
    if (!memberId) {
      message.error('学员信息不完整');
      return;
    }

    try {
      setLoading(true);
      // 将多选数组转为逗号分割的字符串存储
      const payload = {
        ...archiveData,
        expectedGoal: Array.isArray(archiveData.expectedGoal) 
          ? archiveData.expectedGoal.join(',') 
          : (archiveData.expectedGoal || ''),
        babyId: memberId
      };

      const response = await apiClient.post('/sports-server-prod/taller.api.v1/growth/add', payload);
      
      if (response.data.code === 200) {
        message.success('档案保存成功');
        // 重新获取数据以更新视图
        await fetchArchiveData(memberId);
        // 保存其他数据
        await saveExpandData(memberId);
        
        // 调用保存成功的回调
        if (onSaveSuccess) {
          onSaveSuccess();
        }
      } else {
        message.error(response.data.msg || '保存失败');
      }
    } catch (error) {
      console.error('保存档案错误:', error);
      message.error('保存档案失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandChange = (group, code, value) => {
    setExpandData(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [code]: value
      }
    }));
  };

  // 组件加载时获取姿态评估配置数据
  useEffect(() => {
    if (apiClient) {
      fetchPostureAssessmentData();
    }
  }, [apiClient, fetchPostureAssessmentData]);

  // 当memberId变化时重新获取数据
  useEffect(() => {
    if (memberId) {
      fetchArchiveData(memberId);
      fetchExpandData(memberId);
    } else {
      setArchiveData({});
      setExpandData({});
    }
  }, [memberId, fetchArchiveData, fetchExpandData]);

  // 渲染档案表单
  const renderArchiveForm = () => {
    if (!memberId || !memberInfo) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <h3>未选择学员</h3>
        </div>
      );
    }

    return (
      <Spin spinning={loading}>
        <div>
          <Descriptions title="基本信息" bordered column={3}>
            <Descriptions.Item label="姓名">{memberInfo.babyName}</Descriptions.Item>
            <Descriptions.Item label="性别">{memberInfo.babySex === '1' ? '男' : '女'}</Descriptions.Item>
            <Descriptions.Item label="出生年月">{dayjs(memberInfo.babyBirthday).format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label="身高">
              {readonly ? (
                <span>{archiveData.height || ''} cm</span>
              ) : (
                <Input 
                  value={archiveData.height || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, height: value});
                    }
                  }} 
                  addonAfter="cm"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="体重">
              {readonly ? (
                <span>{archiveData.weight || ''} kg</span>
              ) : (
                <Input 
                  value={archiveData.weight || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, weight: value});
                    }
                  }} 
                  addonAfter="kg"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="BMI指数">
              {readonly ? (
                <span>{archiveData.bmi || ''}</span>
              ) : (
                <Input 
                  value={archiveData.bmi || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, bmi: value});
                    }
                  }} 
                />
              )}
            </Descriptions.Item>
          </Descriptions>
          
          <Descriptions title="身体数据" bordered column={4} style={{ marginTop: 16 }}>
            <Descriptions.Item label="身高百分比">
              {readonly ? (
                <span>{archiveData.heightTh || ''}%</span>
              ) : (
                <Input 
                  value={archiveData.heightTh || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, heightTh: value});
                    }
                  }} 
                  addonAfter="%"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="体重百分比">
              {readonly ? (
                <span>{archiveData.weightTh || ''}%</span>
              ) : (
                <Input 
                  value={archiveData.weightTh || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, weightTh: value});
                    }
                  }} 
                  addonAfter="%"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="父亲身高">
              {readonly ? (
                <span>{archiveData.heightF || ''} cm</span>
              ) : (
                <Input 
                  value={archiveData.heightF || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, heightF: value});
                    }
                  }} 
                  addonAfter="cm"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="母亲身高">
              {readonly ? (
                <span>{archiveData.heightM || ''} cm</span>
              ) : (
                <Input 
                  value={archiveData.heightM || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, heightM: value});
                    }
                  }} 
                  addonAfter="cm"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="遗传身高">
              {readonly ? (
                <span>{archiveData.geneticHeight || ''} cm</span>
              ) : (
                <Input 
                  value={archiveData.geneticHeight || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, geneticHeight: value});
                    }
                  }} 
                  addonAfter="cm"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="骨龄">
              {readonly ? (
                <span>{archiveData.boneAge || ''} 岁</span>
              ) : (
                <Input 
                  value={archiveData.boneAge || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, boneAge: value});
                    }
                  }} 
                  addonAfter="岁"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="期望身高">
              {readonly ? (
                <span>{archiveData.desiredHeight || ''} cm</span>
              ) : (
                <Input 
                  value={archiveData.desiredHeight || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, desiredHeight: value});
                    }
                  }} 
                  addonAfter="cm"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="预计未来身高">
              {readonly ? (
                <span>{archiveData.futureHeight || ''} cm</span>
              ) : (
                <Input 
                  value={archiveData.futureHeight || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许数字和小数点，最多两位小数
                    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setArchiveData({...archiveData, futureHeight: value});
                    }
                  }} 
                  addonAfter="cm"
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="运动预期目标" span={4}>
              {readonly ? (
                <span>{Array.isArray(archiveData.expectedGoal) ? archiveData.expectedGoal.join(', ') : (archiveData.expectedGoal || '')}</span>
              ) : (
                <Checkbox.Group 
                  value={archiveData.expectedGoal || []} 
                  onChange={checkedValues => setArchiveData({...archiveData, expectedGoal: checkedValues})}
                >
                  <Checkbox value="运动增高">运动增高</Checkbox>
                  <Checkbox value="运动减肥">运动减肥</Checkbox>
                  <Checkbox value="体态矫正">体态矫正</Checkbox>
                  <Checkbox value="锻炼身体">锻炼身体</Checkbox>
                  <Checkbox value="性格养成">性格养成</Checkbox>
                </Checkbox.Group>
              )}
            </Descriptions.Item>
          </Descriptions>
          
          <Descriptions title="个性信息" bordered column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="性格">
              {readonly ? (
                <span>{archiveData.personality || ''}</span>
              ) : (
                <Input 
                  value={archiveData.personality || ''} 
                  onChange={e => setArchiveData({...archiveData, personality: e.target.value})} 
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="爱好">
              {readonly ? (
                <span>{archiveData.hobby || ''}</span>
              ) : (
                <Input 
                  value={archiveData.hobby || ''} 
                  onChange={e => setArchiveData({...archiveData, hobby: e.target.value})} 
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="运动能力">
              {readonly ? (
                <Rate 
                  value={parseInt(archiveData.locomotionLevel) || 0} 
                  disabled
                />
              ) : (
                <Rate 
                  value={parseInt(archiveData.locomotionLevel) || 0} 
                  onChange={value => setArchiveData({...archiveData, locomotionLevel: value})} 
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="专注力">
              {readonly ? (
                <Rate 
                  value={parseInt(archiveData.focusPowerLevel) || 0} 
                  disabled
                />
              ) : (
                <Rate 
                  value={parseInt(archiveData.focusPowerLevel) || 0} 
                  onChange={value => setArchiveData({...archiveData, focusPowerLevel: value})} 
                />
              )}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="体育成绩" bordered column={2} style={{ marginTop: 16 }}>
            {sportsPerformanceConfig.map(item => (
              <Descriptions.Item label={item.name} key={item.code}>
                {readonly ? (
                  <span>{expandData['体育成绩']?.[item.code] || ''} {item.unit}</span>
                ) : (
                  <Input
                    value={expandData['体育成绩']?.[item.code] || ''}
                    onChange={e => handleExpandChange('体育成绩', item.code, e.target.value)}
                    addonAfter={item.unit}
                  />
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>

          <Descriptions title="运动能力评估" bordered column={1} style={{ marginTop: 16 }}>
            {motorSkillsConfig.map(item => (
              <Descriptions.Item label={item.name} key={item.code}>
                {readonly ? (
                  <span>{expandData['运动能力评估']?.[item.code] || ''}</span>
                ) : (
                  <Radio.Group
                    value={expandData['运动能力评估']?.[item.code] || ''}
                    onChange={e => handleExpandChange('运动能力评估', item.code, e.target.value)}
                  >
                    <Radio value="A+">A+</Radio>
                    <Radio value="A">A</Radio>
                    <Radio value="B">B</Radio>
                    <Radio value="C">C</Radio>
                  </Radio.Group>
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>

          <Descriptions title="体态评估" bordered column={1} style={{ marginTop: 16 }}>
            {Object.entries(postureAssessmentData).map(([categoryName, items]) => (
              <Descriptions.Item 
                label={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{!/_\d+$/.test(categoryName) ? categoryName : '\u00A0'}</span>
                    {!readonly && (
                      <Button 
                        type="link" 
                        size="small" 
                        onClick={() => handleExpandChange('体态评估', categoryName, '')}
                        style={{ padding: '0 8px' }}
                      >
                        清除
                      </Button>
                    )}
                  </div>
                } 
                key={categoryName}
              >
                {readonly ? (
                  <span>{expandData['体态评估']?.[categoryName] || '正常'}</span>
                ) : (
                  <Radio.Group
                    value={expandData['体态评估']?.[categoryName] || ''}
                    onChange={e => handleExpandChange('体态评估', categoryName, e.target.value)}
                  >
                    {items.map(item => (
                      <Radio key={item.code} value={item.name}>
                        {item.name}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>

          <Descriptions title="运动情况" bordered column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="运动周期">
              {readonly ? (
                <span>{expandData['运动情况']?.exerciseCycle || ''}</span>
              ) : (
                <Select
                  value={expandData['运动情况']?.exerciseCycle || ''}
                  onChange={value => handleExpandChange('运动情况', 'exerciseCycle', value)}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="4">4周</Select.Option>
                  <Select.Option value="8">8周</Select.Option>
                </Select>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="运动强度">
              {readonly ? (
                <span>{expandData['运动情况']?.exerciseIntensity || ''}</span>
              ) : (
                <Select
                  value={expandData['运动情况']?.exerciseIntensity || ''}
                  onChange={value => handleExpandChange('运动情况', 'exerciseIntensity', value)}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="低强度">低强度</Select.Option>
                  <Select.Option value="中强度">中强度</Select.Option>
                  <Select.Option value="高强度">高强度</Select.Option>
                </Select>
              )}
            </Descriptions.Item>
          </Descriptions>

          <div style={{ marginTop: 16 }}>
            <h4>身高预测 
              {!readonly && (
                <Button 
                  type="link" 
                  onClick={() => setShowHeightDetails(!showHeightDetails)}
                  style={{ marginLeft: 8 }}
                >
                  {showHeightDetails ? '隐藏详情' : '查看详情'}
                </Button>
              )}
            </h4>
            <div style={{ height: 300, marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[
                    { name: '第一年', height: parseFloat(archiveData.futureHeightTh1) || 0 },
                    { name: '第二年', height: parseFloat(archiveData.futureHeightTh2) || 0 },
                    { name: '第三年', height: parseFloat(archiveData.futureHeightTh3) || 0 },
                    { name: '第四年', height: parseFloat(archiveData.futureHeightTh4) || 0 }
                  ]}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="height" stroke="#8884d8" activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {showHeightDetails && !readonly && (
              <Row gutter={16}>
                <Col span={6}>
                  <div>第一年: 
                    <Input 
                      value={archiveData.futureHeightTh1 || ''} 
                      onChange={e => {
                        const value = e.target.value;
                        // 只允许数字和小数点，最多两位小数
                        if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                          setArchiveData({...archiveData, futureHeightTh1: value});
                        }
                      }} 
                      addonAfter="cm"
                    />
                  </div>
                </Col>
                <Col span={6}>
                  <div>第二年: 
                    <Input 
                      value={archiveData.futureHeightTh2 || ''} 
                      onChange={e => {
                        const value = e.target.value;
                        // 只允许数字和小数点，最多两位小数
                        if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                          setArchiveData({...archiveData, futureHeightTh2: value});
                        }
                      }} 
                      addonAfter="cm"
                    />
                  </div>
                </Col>
                <Col span={6}>
                  <div>第三年: 
                    <Input 
                      value={archiveData.futureHeightTh3 || ''} 
                      onChange={e => {
                        const value = e.target.value;
                        // 只允许数字和小数点，最多两位小数
                        if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                          setArchiveData({...archiveData, futureHeightTh3: value});
                        }
                      }} 
                      addonAfter="cm"
                    />
                  </div>
                </Col>
                <Col span={6}>
                  <div>第四年: 
                    <Input 
                      value={archiveData.futureHeightTh4 || ''} 
                      onChange={e => {
                        const value = e.target.value;
                        // 只允许数字和小数点，最多两位小数
                        if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                          setArchiveData({...archiveData, futureHeightTh4: value});
                        }
                      }} 
                      addonAfter="cm"
                    />
                  </div>
                </Col>
              </Row>
            )}
          </div>
          
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4>专家建议</h4>
              {!readonly && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setExpertAdviceViewMode((prev) => !prev)}
                >
                  {expertAdviceViewMode ? '编辑' : '查看'}
                </Button>
              )}
            </div>
            {expertAdviceViewMode ? (
              <div
                style={{
                  minHeight: 80,
                  padding: '8px 12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  background: '#fafafa',
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                }}
                dangerouslySetInnerHTML={{
                  __html: normalizeRichText(archiveData.expertAdvice || ''),
                }}
              />
            ) : (
              <ReactQuill
                value={archiveData.expertAdvice || ''}
                onChange={(value) => setArchiveData({...archiveData, expertAdvice: value})}
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: 200 }}
              />
            )}
          </div>
          
          {!readonly && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Popconfirm
                title="确认保存"
                description="确定要保存档案信息吗？"
                onConfirm={saveArchiveData}
                okText="确定"
                cancelText="取消"
              >
                <Button type="primary" loading={loading}>
                  保存
                </Button>
              </Popconfirm>
            </div>
          )}
        </div>
      </Spin>
    );
  };

  return (
    <div>
      {renderArchiveForm()}
    </div>
  );
};

export default StudentArchive;